import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';

/**
 * UI-only state for the Workspace dynamic table (per
 * WORKSPACE_IMPLEMENTATION_PLAN.md section 4): which column-CRUD modal is
 * open, which column/cell is active, and display settings.
 */

interface WorkspaceUIState {
  modals: {
    addColumn: boolean;
    editColumn: boolean;
    deleteColumn: boolean;
  };
  activeColumnId: string | null;
  editingCellId: string | null;
  /**
   * The column whose header was last clicked, outlined down the whole grid —
   * Infynd-FE's `selectedColumnKey`. Purely a reading aid: with thirty-odd
   * columns of enrichment output, following one field down the rows is the
   * thing the grid is worst at, and this is what makes it possible.
   *
   * Holds the column's `id` (what the column defs key on), not its `key`.
   */
  selectedColumnId: string | null;
  settings: {
    rowHeight: number;
    showGridLines: boolean;
  };
}

const initialState: WorkspaceUIState = {
  modals: {
    addColumn: false,
    editColumn: false,
    deleteColumn: false,
  },
  activeColumnId: null,
  editingCellId: null,
  selectedColumnId: null,
  settings: {
    rowHeight: 40,
    showGridLines: true,
  },
};

const workspaceUISlice = createSlice({
  name: 'workspaceUI',
  initialState,
  reducers: {
    openModal(state, action: PayloadAction<keyof WorkspaceUIState['modals']>) {
      state.modals[action.payload] = true;
    },
    closeModal(state, action: PayloadAction<keyof WorkspaceUIState['modals']>) {
      state.modals[action.payload] = false;
    },
    setActiveColumn(state, action: PayloadAction<string | null>) {
      state.activeColumnId = action.payload;
    },
    setEditingCell(state, action: PayloadAction<string | null>) {
      state.editingCellId = action.payload;
    },
    /** Clicking the already-selected column's header clears it, so the
     *  outline is dismissable from the same control that raised it. */
    toggleSelectedColumn(state, action: PayloadAction<string>) {
      state.selectedColumnId = state.selectedColumnId === action.payload ? null : action.payload;
    },
    /**
     * Unconditional set, for highlighting a column the user didn't click —
     * a freshly added one. Separate from the toggle above because toggling
     * would *clear* the highlight in the case where someone adds the same
     * column twice, or re-runs an enrichment onto a column already selected.
     */
    setSelectedColumn(state, action: PayloadAction<string>) {
      state.selectedColumnId = action.payload;
    },
    clearSelectedColumn(state) {
      state.selectedColumnId = null;
    },
    updateSettings(state, action: PayloadAction<Partial<WorkspaceUIState['settings']>>) {
      state.settings = { ...state.settings, ...action.payload };
    },
  },
});

export const {
  openModal,
  closeModal,
  setActiveColumn,
  setEditingCell,
  toggleSelectedColumn,
  setSelectedColumn,
  clearSelectedColumn,
  updateSettings,
} = workspaceUISlice.actions;

export const selectWorkspaceModals = (state: RootState) => state.workspaceUI.modals;
export const selectWorkspaceEditingCellId = (state: RootState) => state.workspaceUI.editingCellId;
export const selectWorkspaceSelectedColumnId = (state: RootState) => state.workspaceUI.selectedColumnId;

export default workspaceUISlice.reducer;
